import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../Head';
import Hero from '../Hero';
import * as developmentCss from './Development.m.css';
import * as commonCss from '../../Common.m.css';
import * as css from './EnterpriseCloudServices.m.css';
import Summary from '../Summary';
import ConnectStrip from '../ConnectStrip';
import FAQ from '../FAQ';

const analysisImg = require('../../assets/images/services/analysis.svg');
const assessmentImg = require('../../assets/images/services/assessment.svg');
const backendServicesImg = require('../../assets/images/development/back-end-services.svg');
const cloudDataImg = require('../../assets/images/services/cloud-data.svg');
const cloudOptimisationImg = require('../../assets/images/development/cloud-optimization.svg');
const cloudSecurityImg = require('../../assets/images/services/cloud-security.svg');
const cloudStrategyImg = require('../../assets/images/services/cloud-strategy.svg');
const databaseDevelopmentImg = require('../../assets/images/development/database-development.svg');
const databaseTechnologyImg = require('../../assets/images/development/database-technology.svg');
const enterpriseIntegrationImg = require('../../assets/images/development/enterprise-application-integration.svg');
const frontendToolsImg = require('../../assets/images/development/front-end-tools.svg');
const genericImg = require('../../assets/images/services/generic.png');
const performanceTestingImg = require('../../assets/images/services/performance-testing.svg');
const reduceCostsImg = require('../../assets/images/services/reduce-costs.svg');
const securityImg = require('../../assets/images/services/security-circle.svg');
const sitepenLogo = require('../../assets/images/logos/sitepen.svg');
const toolsImg = require('../../assets/images/services/tools.png');

const factory = create();

export const EnterpriseCloudServices = factory(function EnterpriseCloudServices() {
	return (
		<div classes={developmentCss.root}>
			<Head
				title="Enterprise Cloud Services &amp; Strategy | SitePen"
				description="Enterprise cloud solutions from SitePen allow companies access to virtual IT resources from their cloud infrastructure. Learn more about our cloud services today."
			/>
			<div classes={[developmentCss.heroWrapper]}>
				<div classes={commonCss.contentWrapper}>
					<Hero>
						{{
							xlTitle: 'Enterprise Cloud Services'
						}}
					</Hero>
				</div>
			</div>
			<section classes={commonCss.section}>
				<div classes={commonCss.contentWrapper}>
					<Summary reverse collapse="large">
						{{
							title: 'Utilize the Cloud',
							blurb: (
								<p>
									Adopting cloud virtualization technology allows businesses to
									deliver solutions to customers in shorter time frames than
									traditional infrastructure allows. Cloud solutions can also be
									scaled out more quickly and more cost-effectively to meet
									ongoing changes in user demand. Organizations can build out
									cloud solutions internally by virtualizing existing in-house
									compute and storage infrastructure. Alternatively, they can
									fully externalize their cloud infrastructure needs to
									third-party cloud vendors, freeing them from the overhead of
									infrastructure and datacenter management. Cloud vendors provide
									a wealth of services and capabilities that fulfill large
									commodity middleware requirements for delivering robust and
									secure internet-scale software solutions.
								</p>
							),
							image: (
								<img
									src={cloudOptimisationImg}
									classes={commonCss.responsiveImage}
									alt=""
								/>
							)
						}}
					</Summary>
					<Summary collapse="large" margin={false}>
						{{
							title: 'Scale Your Business With Enterprise Cloud Solutions',
							blurb: (
								<p>
									Infrastructure management has traditionally been the area of
									most significant inertia for software enterprises. When a
									business manages its hardware internally, early planning and
									capacity hedging is needed to ensure products reach the market
									on time and can robustly scale to meet user demand. The business
									must also simultaneously try to minimize unused capacity, only
									spending for infrastructure that is needed. Cloud services help
									to address these issues, allowing companies to quickly scale
									compute and storage infrastructure to meet their exact needs at
									any given point in time.
								</p>
							),
							image: (
								<img src={analysisImg} classes={commonCss.responsiveImage} alt="" />
							)
						}}
					</Summary>
				</div>
			</section>
			<section classes={[commonCss.section, commonCss.shaded]}>
				<div classes={commonCss.contentWrapper}>
					<h2>Challenges Of Cloud Adoption</h2>
					<p classes={[commonCss.largeMarginBottom, commonCss.paragraphWidth]}>
						Depending on your organization’s size, there can be many potential
						challenges to adopting a cloud-based delivery strategy. While these can be
						complex, the benefits of adopting a cloud solution delivery strategy often
						outweigh the effort required to overcome the challenges.
					</p>
				</div>
				<div classes={commonCss.smallContentWrapper}>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Operations',
							blurb:
								'Operations teams will need to adapt to a more DevOps way of working, aligning more closely with development teams to describe and manage infrastructure in a similar manner to how software is created and operated, including more automation of infrastructure scale-out and configuration. Monitoring and alerting concepts will need to be adapted to work within the desired cloud environment, including any necessary aggregation if adopting a hybrid cloud model that includes monitoring existing infrastructure.',
							image: (
								<img
									src={databaseTechnologyImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Networking',
							blurb:
								'Networking challenges typically relate to any bridging that may be required when operating hybrid cloud models, where existing infrastructure in your organization needs to communicate with new systems and networks within a cloud provider. Data transfer requirements need to be well understood, both for ongoing bandwidth cost implications and whether additional physical network infrastructure is required. For organizations with large data transfer requirements, this could include installing new dedicated connections to allow more direct routing between internal data centers and third-party cloud providers.',
							image: (
								<img
									src={databaseDevelopmentImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Security',
							blurb:
								'Security remains an essential factor regardless of an organization’s infrastructure choice. Cloud adoption brings challenges in this area around new means of implementing your existing security policies and new policy adherence assessment methods. Additional care is also needed when choosing a hybrid cloud model where security practices need to bridge between in-house and cloud-native implementations. All affected teams will require new security training to understand their expanded responsibilities when working within the new cloud environment.',
							image: (
								<img
									src={securityImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Development',
							blurb:
								'When adopting a cloud delivery strategy, your development teams may need additional training to learn about infrastructure and cloud services concepts specific to the chosen cloud vendor. While these concepts are similar across vendors at a high level, there are implementation details that are specific to each. In addition to expanded security training, development teams will also need to be aware of cost management - not an area of regular focus for developers. These areas of training are required to ensure pre-production cloud resources are responsibly managed and remain within budget.',
							image: (
								<img
									src={performanceTestingImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary trailingBias="large" smaller margin={false}>
						{{
							title: 'Cost',
							blurb:
								'Cost-effectiveness remains an ongoing challenge. Careful monitoring and management of cloud solution costs are required to ensure the cloud solutions remain more cost-effective than an equivalent on-premise implementation. A cost risk is especially relevant within pre-production environments where solution teams may spin up what should be temporary resources for a once-off task but forget to shut the resources down once completed. Without sufficient oversight, these costs can quickly become unconstrained. Cloud vendors provide a range of tools for cost insight and management - these, together with tight scaling to meet exact user demand, allow organizations to keep on top of their cloud spend and ensure it remains below that of a do-it-yourself approach.',
							image: (
								<img
									src={reduceCostsImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
				</div>
			</section>
			<section classes={commonCss.section}>
				<div classes={commonCss.contentWrapper}>
					<h3>Our Cloud Services</h3>
					<p classes={commonCss.paragraphWidth}>
						SitePen focuses on end-to-end design and implementation of cloud-native
						applications across a variety of public cloud vendor solutions. Our
						expertise allows us to offer cloud strategy design, including cloud adoption
						and migration options. Additionally, we provide cloud-based solutions within
						enterprise-managed virtual private clouds based on public cloud services.
					</p>
					<Summary>
						{{
							title: (
								<span
									classes={css.strategyAdoptionAnchorTag}
									id="strategy-adoption"
								>
									Cloud Strategy &amp; Adoption
								</span>
							),
							blurb:
								'We work with business, technology, and operations stakeholders in your organization to help design and implement cloud-first strategies and solutions for new projects and help with the migration of existing applications to a more cloud-native target state.',
							image: (
								<img
									src={cloudStrategyImg}
									classes={commonCss.responsiveImage}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
				</div>
				<div classes={commonCss.smallerContentWrapper}>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Assessment',
							blurb:
								'SitePen can help analyze your infrastructure requirements for existing applications and your upcoming product roadmap to identify where you can benefit in ease-of-management, cost-saving, and delivery efficiency through implementing a cloud strategy. We provide assessments that appreciate such benefits while not treating the cloud as the only way forward - our approach balances a wide range of options to meet your technology ecosystem needs.',
							image: <img src={assessmentImg} alt="" loading="lazy" />
						}}
					</Summary>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Planning',
							blurb:
								'With a cloud adoption strategy established, SitePen can help your team in the detailed planning and analysis of all steps required to implement the strategy. We aim to provide grounded roadmaps that consider organizational constraints and are incrementally achievable to deliver ongoing successes while still retaining the flexibility to adapt to any unforeseen challenges.',
							image: <img src={frontendToolsImg} alt="" loading="lazy" />
						}}
					</Summary>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Migration',
							blurb:
								'SitePen’s complete delivery experience can help implement a cloud adoption strategy, either helping alongside your existing technology teams or an independent delivery function. The includes implementing new cloud-first applications that align with a cloud strategy from the start as well as incremental migration of existing on-premise or non-cloud applications to your intended cloud platform.',
							image: <img src={backendServicesImg} alt="" loading="lazy" />
						}}
					</Summary>
					<Summary trailingBias="large" smaller margin={false}>
						{{
							title: 'Optimization',
							blurb:
								'SitePen’s expertise draws from a wide range of cloud solution design and delivery successes within large enterprises, including the architecture, design, implementation, and ongoing production support of cloud-enabled applications. We can help your organization in the continuous assessment and optimization of cloud solution delivery effectiveness, including analyzing adherence to best practices, identifying areas for simplification, looking at cost-effectiveness between alternative options, and identifying areas of cost reduction.',
							image: <img src={enterpriseIntegrationImg} alt="" loading="lazy" />
						}}
					</Summary>
				</div>
				<div classes={commonCss.contentWrapper}>
					<Summary>
						{{
							title: (
								<span
									classes={css.cloudArchitectureAnchorTag}
									id="architecture-design"
								>
									Cloud Architecture Design
								</span>
							),
							blurb:
								'SitePen’s expertise lies in full-stack, cloud-based solution design and implementation that serve a wide range of industries and scales. We work with a range of product stakeholders and help analyze your requirements to find a cloud architecture design comprising the best fit of public cloud infrastructure and capabilities that can make your vision successful. We utilize architecture patterns and methodologies that deliver robust, secure, cost-effective, and scalable cloud solutions that meet a range of goals. With a target state architecture established, we can then work with your engineering teams to implement the solution. Our end-to-end solution delivery capability can handle the full implementation if required.',
							image: (
								<img
									src={toolsImg}
									classes={commonCss.responsiveImage}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary reverse>
						{{
							title: (
								<span
									classes={css.migrationManagementAnchorTag}
									id="migration-management"
								>
									Cloud Migration Management
								</span>
							),
							blurb:
								'Depending on your needs, you may have existing in-house infrastructure that you wish to continue using in some capacity. Perhaps you are looking to migrate existing on-premise solutions to the cloud. SitePen can help you in this process to analyze, plan, and implement hybrid cloud architecture or any migration and uplift of existing on-premises applications to the cloud if you wish to move fully onto the cloud. Our services include the full redesign and implementation of new cloud-native replacements or designing more incremental migration strategies that progressively move components of existing solutions over to cloud-based equivalents. Our risk assessment expertise can help feed decisions between various migration options to reach the most effective strategy for your particular cloud migration or hybrid cloud requirements.',
							image: (
								<img
									src={cloudDataImg}
									classes={commonCss.responsiveImage}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary margin={false}>
						{{
							title: (
								<span
									classes={css.cloudIntegrationAnchorTag}
									id="enterprise-cloud-integration"
								>
									Enterprise Cloud Integration
								</span>
							),
							blurb:
								'SitePen can help design and implement cloud-based applications that require integration with on-premise data, services, or external third-party integrations. Our aims with integration solutions are to achieve high performance and availability while minimizing costs associated with crossing boundaries between internal enterprise systems and public cloud services.',
							image: (
								<img
									src={genericImg}
									classes={commonCss.responsiveImage}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
				</div>
			</section>
			<section classes={[commonCss.section, commonCss.shaded]}>
				<div classes={commonCss.smallContentWrapper}>
					<img
						src={cloudOptimisationImg}
						classes={[commonCss.responsiveImage, commonCss.centeredHeaderImage]}
						alt=""
						loading="lazy"
					/>
					<h3>Cloud Benefits</h3>
					<p>
						Cloud services significantly reduce the gap between software development
						agility and the inertia of hardware procurement, configuration, and
						management. Compute and storage can be operated as virtualized commodities
						and provisioned in a matter of minutes to meet changing requirements
						throughout the lifecycle of an application. The ability to spin up and scale
						entire environments at minimum cost and time can significantly improve your
						SDLC's effectiveness.
					</p>
					<p>
						Increased agility helps during product development to quickly prototype
						various options where implementation teams may not yet know what an optimum
						solution would be. This agility also translates to live applications on
						production platforms where infrastructure resources can dynamically scale in
						response to user demand - maximizing usage while minimizing cost and
						ensuring the best possible end-user experience.
					</p>
					<h3 classes={commonCss.smallerPaddingTop}>
						Cloud-Ready Enterprise Applications
					</h3>
					<p>
						Applications designed as "cloud-first" can better meet changing business and
						market conditions in a timely and cost-effective manner by reducing the
						overhead of peripheral technology functions typically associated with
						software delivery. Meeting business and customer needs can become more of a
						focus, freeing solution teams to deliver the best possible experience for
						all their users located anywhere in the world.
					</p>
				</div>
			</section>
			<section classes={[commonCss.section, commonCss.contentWrapper]}>
				<Summary reverse>
					{{
						title: 'Cloud Security',
						blurb: (
							<virtual>
								<p>
									Information security is critical to many software solutions,
									increasingly so in a modern environment of breaches and
									information leaks. Such an event can instantly wipe out the
									trust that an organization has taken years to build up with
									their users. Businesses that have not woken up to the severity
									of these risks will likely face harsh consequences in the
									future.
								</p>
								<p>
									Our agency is acutely aware of the reputational damage that poor
									security can cause. We treat security with paramount importance
									within every one of our solutions. Secure design and
									implementation principles are intrinsic to every step of our
									solution delivery process - including considerations of data
									sensitivity classification and residency requirements, secure
									network design and cloud services utilization, and authorization
									across all solution layers and integration points.
								</p>
							</virtual>
						),
						image: (
							<img
								src={cloudSecurityImg}
								classes={commonCss.responsiveImage}
								alt=""
								loading="lazy"
							/>
						)
					}}
				</Summary>
				<Summary margin={false}>
					{{
						title: 'Why Choose SitePen?',
						blurb:
							'SitePen provides a wealth of expertise in the design, implementation, and delivery of cloud-first software solutions. We can help your organization navigate the complexities of public cloud vendor offerings to find the most appropriate solution for your needs. Our team can help at every stage of cloud adoption, including strategy formulation, enterprise and solution architecture, cloud infrastructure design, and solution implementation and delivery.',
						image: (
							<img
								src={sitepenLogo}
								classes={commonCss.responsiveImage}
								alt=""
								loading="lazy"
							/>
						)
					}}
				</Summary>
			</section>
			<ConnectStrip />
			<FAQ title="Cloud Strategy and Adoption FAQs">
				{[
					{
						title: 'Is Cloud Adoption Secure?',
						content: (
							<virtual>
								<p>
									In short, yes. Cloud providers can invest significant resources
									into providing a suite of design principles and implementation
									resources that meet some of the highest industry security needs.
									Typically these solutions offer far greater security than many
									organizations require or can implement themselves. Adopting
									cloud-first application development and a cloud hosting strategy
									allows the implementation of greater security than your
									organization may currently support - for far less investment
									than uplifting in-house security would entail.
								</p>
								<p>
									However, cloud operations and development teams within your
									organization still have a responsibility to design and implement
									secure application and networking practices within the cloud
									solutions they produce and maintain. A security chain is only as
									good as its weakest link, so secure methods and ongoing due
									diligence are always required - this fact remains valid for both
									on-premise and cloud solutions.
								</p>
							</virtual>
						)
					},
					{
						title: 'What Are the Benefits of a Cloud Adoption Strategy?',
						content: (
							<p>
								Agility and scalability are typically the primary benefits of using
								virtualized infrastructure within a cloud provider. Cloud solutions
								reduce infrastructure procurement, configuration, and scaling
								inertia, bringing it closer to software development. Solution
								implementation teams can quickly spin up resources when implementing
								new requirements or scaling existing capabilities to meet user
								demand. Scalability can even be automated, allowing precise
								infrastructure provisioning to meet fluctuating demand over time -
								users receive the application experience they expect whenever they
								need it. Organizations minimize their costs when demand decreases
								for their typical usage cycles. There can also be significant
								cost-reduction benefits when organizations reduce the burden of
								maintaining their own infrastructure and data centers.
							</p>
						)
					},
					{
						title: 'What are Enterprise Cloud Services?',
						content: (
							<virtual>
								<p>
									Enterprise cloud services provide a middle ground between legacy
									server-focused infrastructure, managed internally within an
									organization, and fully outsourced compute and storage
									infrastructure managed by third-party cloud services providers.
									Enterprises can offer internal cloud services by virtualizing
									the compute and storage clusters within their own data centers.
									This middle ground often appeals to organizations that want to
									maintain complete internal control of their products, data, and
									infrastructure while still providing the benefits that solution
									implementation teams expect from a cloud services provider.
								</p>
								<p>
									However, many such organizations have realized that the complete
									externalization of infrastructure management to a third-party
									provider can mean significant cost savings and more robust
									solution implementations. Cloud providers are increasingly able
									to offer solutions that exceed robustness, scalability,
									locality, and security requirements - all at a more competitive
									price point than organizations can manage internally.
								</p>
								<p>
									Our agency focuses on solutions based on public cloud vendors
									rather than internally-managed enterprise cloud services.
									However, we can help establish strategies for migration from
									private internal enterprise clouds to public cloud equivalents.
								</p>
							</virtual>
						)
					},
					{
						title: 'Why Are Enterprises Moving to the Cloud?',
						content: (
							<p>
								The benefits of cloud adoption are clear. While infrastructure is a
								critical part of every successful software product, it provides
								little inherent value if considered a standalone concept. A
								product’s value derives from the data it uses and the intellectual
								property of its software implementation, not the infrastructure used
								to run it. Businesses that can focus on this value come to realize
								the commodity nature of infrastructure. Managing compute and storage
								infrastructure internally within an organization is likely not an
								option for startups looking for maximum agility. It is also becoming
								increasingly difficult for large enterprises to justify when they
								are looking at reducing costs through consolidation around their
								businesses' value-generating areas.
							</p>
						)
					}
				]}
			</FAQ>
			<FAQ title="Cloud Architecture and Design FAQs">
				{[
					{
						title: 'What Are Cloud Design Principles?',
						content: (
							<p>
								Specific design principles are required when designing cloud-first
								applications to realize the full benefits that cloud platforms
								allow. These are based on axioms around the commoditization of
								infrastructure and capabilities that refine many pre-existing
								technology industry concepts. Principles such as simplification and
								decoupling, where discrete services and capabilities are created
								based around single responsibilities and loosely coupled together,
								are crucial to creating a maintainable and scalable solution.
							</p>
						)
					},
					{
						title: 'Why Is Cloud Architecture Design Important?',
						content: (
							<virtual>
								<p>
									Just as with any software system, correct understanding and
									utilization of infrastructure and commodity middleware
									capabilities are critical for its success. Insufficient or
									misconfigured infrastructure can easily result from a lack of
									knowledge of the underlying software architecture design or lack
									of capacity planning around the system’s expected usage and
									scalability requirements. This misalignment of software
									requirements versus infrastructure provisioned can cause a range
									of problems, including an inability to meet user demand and
									budgetary waste.
								</p>
								<p>
									It is essential to understand how a system is composed, each
									component’s performance characteristics, and the size and shape
									of data at each stage of the overall data flow. Having this
									knowledge allows a cloud architect to properly select and
									configure infrastructure and middleware to realize the software
									solution’s full value potential.
								</p>
							</virtual>
						)
					},
					{
						title: 'What is Multi-Cloud Architecture?',
						content: (
							<p>
								Some organizations want additional flexibility in how their
								solutions are hosted, preferring to rely on a range of cloud vendor
								platforms instead of typical single vendor lock-in. Utilizing
								multi-cloud architecture comes with additional challenges and
								overheads to maintain. Still, it can provide benefits such as using
								the most cost-effective offering at any given time and increased
								resiliency. The extra layer of cloud concept virtualization typical
								of a multi-cloud architecture can also help when migrating between
								different cloud vendors. Organizations can operate their solutions
								within a single vendor but avoid excessive lock-in and any need to
								re-implement solutions if they reach a point where an alternative
								cloud vendor will provide a more beneficial service.
							</p>
						)
					},
					{
						title: 'What Is Cloud Data Management?',
						content: (
							<virtual>
								<p>
									The value of a software product is derived mainly from the data
									that it processes. Enterprises are beginning to appreciate this
									fact and are implementing data-focused strategies rather than
									those based around individual product silos. These strategies
									typically involve identifying, externalizing, unifying, and
									connecting datasets across the broader organization. Both
									internal and customer products can then access the centralized
									data authorities and combine them in innovative ways, exposing
									higher value than the sum of the individual parts.
								</p>
								<p>
									Implementations can take the form of data services that provide
									APIs for external solutions to integrate with, including
									customer-facing integrations where data APIs become marketable
									products. More complex implementations may involve "data lakes,"
									where central storage capabilities provide large scale data
									ingestion and access mechanisms to application development
									teams. These capabilities can be self-service or managed via
									centralized groups that hold authority for particular datasets.
									Cloud data management solutions are typically well suited to
									"big data,” providing compute capabilities that can run
									transformation, derivation, or similar data operations adjacent
									to or within the data storage layer itself. Thus, maximizing
									performance when dealing with internet-scale datasets.
								</p>
							</virtual>
						)
					}
				]}
			</FAQ>
		</div>
	);
});

export default EnterpriseCloudServices;
